import {
  Inventory,
} from "../types/supplyChain";
import {
  AvgCostByCountryOverTimeResponse,
  CentralStockManagementResponse,
  CentralStockSummaryResponse,
  ClearCacheResponse,
  CnWarehouseByCountryResponse,
  CreateShipmentsFromOrderResponse,
  DbHealthResponse,
  ForecastResponse,
  LocalStockManagementResponse,
  LocalStockSummaryResponse,
  OrderCompositionResponse,
  OrderDetailResponse,
  OrderInProgressDetailResponse,
  OrderMutationResponse,
  OrdersResponse,
  OrdersToBePlacedResponse,
  ProductMixResponse,
  RawDataCombinedResponse,
  RawInventoryDataResponse,
  RawSalesDataResponse,
  RawSkuAllDataResponse,
  RawSkuSalesResponse,
  ReplenishmentCalendarResponse,
  ReplenishmentRecommendationsResponse,
  ShipmentLogsResponse,
  ShipmentMutationResponse,
  ShipmentsResponse,
  ShippingByCountryResponse,
  ShippingCostBandsResponse,
  ShippingCostPerItemResponse,
  ShippingCountriesResponse,
  ShippingVolumeBandsResponse,
  ShippingWeightBandsResponse,
  SkuHistoryResponse,
  SupplierOrderHistoryResponse,
  WarehouseAnalyticsResponse,
} from "../types/api";

// RELATIVE API CALLS: Works for both local and external users
const API_BASE_URL = "/api/v1";

// ClerkProvider attaches itself to window.Clerk asynchronously, so a request
// fired on first page load can race Clerk's own script/session loading - awaiting
// window.Clerk.load() (idempotent, resolves once already loaded) closes that gap.
// Backend middleware requires a Bearer token in production, so every request
// needs it attached here rather than at each of the ~50 call sites below.
const getAuthToken = async (): Promise<string | null> => {
  try {
    const clerk = (window as any).Clerk;
    if (!clerk) return null;
    if (typeof clerk.load === "function" && !clerk.loaded) {
      await clerk.load();
    }
    const token = await clerk.session?.getToken();
    return token || null;
  } catch {
    return null;
  }
};

// Helper function for timeout handling with connection management
const fetchWithTimeout = async (
  url: string,
  options: RequestInit = {},
  timeout = 8000,
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const token = await getAuthToken();

  // Create the request promise
  const requestPromise = fetch(url, {
    ...options,
    signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })
    .then((response) => {
      clearTimeout(timeoutId);
      return response;
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      throw error;
    });

  try {
    return await requestPromise;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
};

//  Prevent multiple concurrent requests to same endpoint
const ongoingRequests = new Map<string, Promise<any>>();

const deduplicatedRequest = async <T>(key: string, requestFn: () => Promise<T>): Promise<T> => {
  // If request is already in progress, return the existing promise
  if (ongoingRequests.has(key)) {
    return ongoingRequests.get(key)!;
  }

  // Start new request and store the promise
  const requestPromise = requestFn().finally(() => {
    // Clean up when request completes
    ongoingRequests.delete(key);
  });

  ongoingRequests.set(key, requestPromise);
  return requestPromise;
};

export const clearCache = async () => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/warehouse/clear-cache`, {
      method: 'POST',
    });
    return response;
  } catch (error) {
    console.error("Error clearing cache:", error);
    throw error;
  }
};

export const supplyChainService = {
  // Utility method to validate SKUs and warehouses
  validateIdentifier(identifier: string, type: "SKU" | "Warehouse"): boolean {
    if (!identifier || typeof identifier !== "string") {
      console.warn(`❌ Invalid ${type}: ${identifier} - not a string`);
      return false;
    }

    if (
      identifier.includes("/") ||
      identifier.includes("\\") ||
      identifier.trim() === ""
    ) {
      console.warn(`❌ Invalid ${type}: ${identifier} - contains invalid characters or is empty`);
      return false;
    }

    return true;
  },

  // Retry mechanism with exponential backoff
  async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000,
  ): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries) {
          throw lastError;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`🔄 Retry attempt ${attempt + 1}/${maxRetries + 1} after ${delay}ms delay`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // This should never be reached due to the throw in the catch block above
    throw new Error(lastError?.message || 'Unknown error occurred');
  },

  // Get inventory with timeout and retry
  async getInventory(skuId: string, warehouse: string): Promise<Inventory> {
    // Validate SKU and warehouse before making request
    if (
      !this.validateIdentifier(skuId, "SKU") ||
      !this.validateIdentifier(warehouse, "Warehouse")
    ) {
      throw new Error(`Invalid SKU (${skuId}) or warehouse (${warehouse})`);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/inventory/${skuId}/${warehouse}`,
        {},
        10000,
      ); // 10 second timeout
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(
            `No inventory data found for SKU ${skuId} in warehouse ${warehouse}`,
          );
        }
        throw new Error(`Failed to fetch inventory: ${response.status}`);
      }
      // Clone the response to prevent "body stream already read" errors
      const clonedResponse = response.clone();
      return await clonedResponse.json();
    });
  },

  // Get China inventory data for a specific SKU
  async getChinaInventory(skuId: string): Promise<Inventory> {
    // Validate SKU before making request
    if (!this.validateIdentifier(skuId, "SKU")) {
      throw new Error(`Invalid SKU: ${skuId}`);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/china/inventory/${skuId}`,
        {},
        10000,
      ); // 10 second timeout
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No China inventory data found for SKU ${skuId}`);
        }
        throw new Error(`Failed to fetch China inventory: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Get warehouses for a specific country
  async getWarehousesForCountry(country: string): Promise<string[]> {
    // Validate country before making request
    if (!this.validateIdentifier(country, "Warehouse")) {
      throw new Error(`Invalid country: ${country}`);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/inventory/warehouses/${country}`,
        {},
        8000,
      ); // 8 second timeout
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No warehouses found for country ${country}`);
        }
        throw new Error(`Failed to fetch warehouses: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Validate SKU and warehouse combination
  async validateSkuWarehouse(skuId: string, warehouse: string): Promise<boolean> {
    // Validate inputs before making request
    if (
      !this.validateIdentifier(skuId, "SKU") ||
      !this.validateIdentifier(warehouse, "Warehouse")
    ) {
      return false;
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/validate/sku-warehouse/${skuId}/${warehouse}`,
        {},
        5000,
      ); // 5 second timeout
      if (!response.ok) {
        return false;
      }
      const validation = await response.json();
      return validation.valid === true;
    });
  },

  // Warehouse Management Endpoints for Client Requirements
  async getLocalStockManagement(
    warehouse: string,
    options?: { page?: number; pageSize?: number; includeSkuBreakdown?: boolean; snapshotDate?: string; dateFrom?: string; dateTo?: string }
  ): Promise<LocalStockManagementResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", String(options.page));
    if (options?.pageSize) params.set("pageSize", String(options.pageSize));
    if (typeof options?.includeSkuBreakdown === "boolean") {
      params.set("includeSkuBreakdown", String(options.includeSkuBreakdown));
    }
    if (options?.snapshotDate) params.set("snapshot_date", options.snapshotDate);
    if (options?.dateFrom) params.set("date_from", options.dateFrom);
    if (options?.dateTo) params.set("date_to", options.dateTo);
    const query = params.toString() ? `?${params.toString()}` : "";
    const requestKey = `local-stock-management-${warehouse}-${query || "default"}`;

    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/local-stock-management/${warehouse}${query}`,
          {},
          120000, // 2 minutes timeout for real data calculation
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch local stock management for ${warehouse}: ${response.status}`);
        }
        const clonedResponse = response.clone();
        return await clonedResponse.json();
      })
    );
  },

  async getCentralStockManagement(
    options?: { page?: number; pageSize?: number; compact?: boolean; includeRawData?: boolean }
  ): Promise<CentralStockManagementResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.set("page", String(options.page));
    if (options?.pageSize) params.set("pageSize", String(options.pageSize));
    if (typeof options?.compact === "boolean") params.set("compact", String(options.compact));
    if (typeof options?.includeRawData === "boolean") params.set("includeRawData", String(options.includeRawData));
    const query = params.toString() ? `?${params.toString()}` : "";
    const requestKey = `central-stock-management-${query || "default"}`;

    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/central-stock-management${query}`,
          {},
          120000, // 2 minutes timeout for data calculation
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch central stock management: ${response.status}`);
        }
        const clonedResponse = response.clone();
        return await clonedResponse.json();
      })
    );
  },

  async getLocalStockManagementSummary(warehouse: string): Promise<LocalStockSummaryResponse> {
    const requestKey = `local-stock-management-summary-${warehouse}`;
    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/local-stock-management-summary/${warehouse}`,
          {},
          30000,
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch local stock summary for ${warehouse}: ${response.status}`);
        }
        return await response.json();
      })
    );
  },

  async getCentralStockManagementSummary(): Promise<CentralStockSummaryResponse> {
    return deduplicatedRequest("central-stock-management-summary", () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/central-stock-management-summary`,
          {},
          30000,
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch central stock summary: ${response.status}`);
        }
        return await response.json();
      })
    );
  },

  async getOrdersToBePlaced(): Promise<OrdersToBePlacedResponse> {
    return deduplicatedRequest("orders-to-be-placed", () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/orders-to-be-placed`,
          {},
          120000, // 2 minutes timeout for data calculation
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch orders to be placed: ${response.status}`);
        }
        const clonedResponse = response.clone();
        return await clonedResponse.json();
      })
    );
  },

  async clearWarehouseCache(): Promise<ClearCacheResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/warehouse/clear-cache`,
        {
          method: 'POST',
        },
        10000, // 10 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to clear warehouse cache: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Alias for backward compatibility
  async clearCache(): Promise<ClearCacheResponse> {
    return this.clearWarehouseCache();
  },

  // Get inventory countries
  async getInventoryCountries(): Promise<string[]> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/inventory/countries`,
        {},
        8000,
      ); // 8 second timeout
      if (!response.ok) {
        throw new Error(`Failed to fetch inventory countries: ${response.status}`);
      }
      return await response.json();
    });
  },

  // RAW DATA: For frontend calculation with TanStack Query caching
  async getRawInventoryData(warehouse: string): Promise<RawInventoryDataResponse> {
    const requestKey = `raw-inventory-data-${warehouse}`;
    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/raw-inventory-data/${warehouse}`,
          {},
          60000, // 1 minute timeout for raw data
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch raw inventory data for ${warehouse}: ${response.status}`);
        }
        return await response.json();
      })
    );
  },

  async getRawSalesData(warehouse: string): Promise<RawSalesDataResponse> {
    const requestKey = `raw-sales-data-${warehouse}`;
    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/raw-sales-data/${warehouse}`,
          {},
          60000, // 1 minute timeout for raw data
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch raw sales data for ${warehouse}: ${response.status}`);
        }
        return await response.json();
      })
    );
  },

  async getRawDataCombined(warehouse: string): Promise<RawDataCombinedResponse> {
    const requestKey = `raw-data-combined-${warehouse}`;
    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/warehouse/raw-data-combined/${warehouse}`,
          {},
          90000, // 90 second timeout - BigQuery queries can be slow on cold start
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch raw combined data for ${warehouse}: ${response.status}`);
        }
        return await response.json();
      }, 2, 2000) // 2 retries with 2 second base delay
    );
  },

  async getRawSkuSalesData(skuId: string, warehouse: string): Promise<RawSkuAllDataResponse> {
    const requestKey = `raw-sales-data-${skuId}-${warehouse}`;
    return deduplicatedRequest(requestKey, () =>
      this.retryWithBackoff(async () => {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}/realtime/raw-data/${encodeURIComponent(skuId)}/${encodeURIComponent(warehouse)}`,
          {},
          20000, // 20 second timeout for faster failure/retry
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch raw sales data for SKU ${skuId}: ${response.status}`);
        }
        return await response.json();
      })
    );
  },

  async getRawSkuAllData(skuId: string, warehouse: string): Promise<RawSkuSalesResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/realtime/raw/all-data/${skuId}?warehouse=${warehouse}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch raw all data for SKU ${skuId}: ${response.status}`);
      }
      return await response.json();
    });
  },

  // SHIPPING ANALYTICS: Last mile shipping analytics endpoints
  // async getShippingAnalyticsOverview(
  //   startDate: string,
  //   endDate: string,
  //   country?: string
  // ): Promise<any> {
  //   const params = new URLSearchParams({
  //     start_date: startDate,
  //     end_date: endDate,
  //   });
  //   if (country) {
  //     params.append('country', country);
  //   }

  //   return this.retryWithBackoff(async () => {
  //     const response = await fetchWithTimeout(
  //       `${API_BASE_URL}/shipping-analytics/overview?${params.toString()}`,
  //       {},
  //       30000, // 30 second timeout
  //     );
  //     if (!response.ok) {
  //       throw new Error(`Failed to fetch shipping analytics overview: ${response.status}`);
  //     }
  //     return await response.json();
  //   });
  // },

  async getShippingAnalyticsByCountry(
    startDate: string,
    endDate: string,
    groupBy: string = 'month',
    country?: string
  ): Promise<ShippingByCountryResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });

    if (country) {
      params.append('country', country);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/by-country?${params.toString()}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch shipping by country: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getShippingCostBands(
    startDate: string,
    endDate: string,
    country?: string,
    origin?: string
  ): Promise<ShippingCostBandsResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (country) {
      params.append('country', country);
    }
    if (origin) {
      params.append('origin', origin);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/cost-bands?${params.toString()}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch cost bands: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getShippingWeightBands(
    startDate: string,
    endDate: string,
    country?: string,
    origin?: string
  ): Promise<ShippingWeightBandsResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (country) {
      params.append('country', country);
    }
    if (origin) {
      params.append('origin', origin);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/weight-bands?${params.toString()}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch weight bands: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getShippingVolumeBands(
    startDate: string,
    endDate: string,
    country?: string
  ): Promise<ShippingVolumeBandsResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (country) {
      params.append('country', country);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/volume-bands?${params.toString()}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch volume bands: ${response.status}`);
      }
      return await response.json();
    });
  },

  // async getShippingTrends(
  //   startDate: string,
  //   endDate: string,
  //   country?: string,
  //   metric: string = 'cost'
  // ): Promise<any> {
  //   const params = new URLSearchParams({
  //     start_date: startDate,
  //     end_date: endDate,
  //     metric: metric,
  //   });
  //   if (country) {
  //     params.append('country', country);
  //   }

  //   return this.retryWithBackoff(async () => {
  //     const response = await fetchWithTimeout(
  //       `${API_BASE_URL}/shipping-analytics/trends?${params.toString()}`,
  //       {},
  //       30000, // 30 second timeout
  //     );
  //     if (!response.ok) {
  //       throw new Error(`Failed to fetch shipping trends: ${response.status}`);
  //     }
  //     return await response.json();
  //   });
  // },

  async getCostPerKgAnalysis(
    startDate: string,
    endDate: string,
    country?: string,
    origin?: string
  ): Promise<ShippingCostPerItemResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (country) {
      params.append('country', country);
    }
    if (origin) {
      params.append('origin', origin);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/cost-per-kg?${params.toString()}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch cost per kg analysis: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getShippingCountries(origin?: string): Promise<ShippingCountriesResponse> {
    const params = new URLSearchParams();
    if (origin) {
      params.append('origin', origin);
    }
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/countries${params.toString() ? `?${params.toString()}` : ''}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch shipping countries: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Warehouse Analytics endpoints
  async getWarehouseAnalytics(
    startDate: string,
    endDate: string,
    origin?: string
  ): Promise<WarehouseAnalyticsResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (origin) {
      params.append('origin', origin);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/by-warehouse?${params.toString()}`,
        {},
        30000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch warehouse analytics: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getOrderComposition(
    startDate: string,
    endDate: string,
    warehouse?: string
  ): Promise<OrderCompositionResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (warehouse) {
      params.append('warehouse', warehouse);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/order-composition?${params.toString()}`,
        {},
        30000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch order composition: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getCnWarehouseByCountry(
    startDate: string,
    endDate: string
  ): Promise<CnWarehouseByCountryResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/cn-warehouse-by-country?${params.toString()}`,
        {},
        30000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch CN warehouse by country: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getAvgCostByCountryOverTime(
    startDate: string,
    endDate: string,
    groupBy: string = 'month',
    origin?: string,
    country?: string
  ): Promise<AvgCostByCountryOverTimeResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    if (origin) {
      params.append('origin', origin);
    }
    if (country) {
      params.append('country', country);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/avg-cost-by-country-over-time?${params.toString()}`,
        {},
        30000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch avg cost by country over time: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getSupplierOrderHistory(
    startDate?: string,
    endDate?: string,
    itemFilter?: string,
    statusFilter?: string
  ): Promise<SupplierOrderHistoryResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (itemFilter) params.append('item_filter', itemFilter);
    if (statusFilter) params.append('status_filter', statusFilter);

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipping-analytics/supplier-order-history?${params.toString()}`,
        {},
        30000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch supplier order history: ${response.status}`);
      }
      return await response.json();
    });
  },

  // REPLENISHMENT RECOMMENDATIONS: Automated order recommendations
  async getReplenishmentRecommendations(
    warehouse: string = 'all',
    monthsAhead: number = 3,
    minPriority: string = 'all'
  ): Promise<ReplenishmentRecommendationsResponse> {
    const params = new URLSearchParams({
      warehouse: warehouse,
      months_ahead: monthsAhead.toString(),
      min_priority: minPriority,
    });

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/replenishment/recommendations?${params.toString()}`,
        {},
        180000, // 3 minutes timeout for calculation (queries all warehouses)
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch replenishment recommendations: ${response.status}`);
      }
      return await response.json();
    });
  },

  // ORDER FORECAST: Get 12-month order forecast
  async getForecast(
    warehouse: string = 'all',
    product?: string
  ): Promise<ForecastResponse> {
    const params = new URLSearchParams({ warehouse });
    if (product) {
      params.append('product', product);
    }

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/forecast?${params.toString()}`,
        {},
        120000, // 2 minutes timeout for forecast calculation
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch order forecast: ${response.status}`);
      }
      return await response.json();
    });
  },


  async getHistoricalStockData(
    skuId: string,
    warehouse: string,
    days: number = 90
  ): Promise<SkuHistoryResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/inventory/sku-history/${skuId}/${warehouse}?days=${days}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch historical stock data for ${skuId}: ${response.status}`);
      }
      return await response.json();
    });
  },

  // ORDER MANAGEMENT: Orders in progress tracking
  async getOrdersInProgress(): Promise<OrdersResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/in-progress`,
        {},
        30000, // 30 second timeout 
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch orders in progress: ${response.status}`);
      }
      return await response.json();
    });
  },

  async createOrderInProgress(orderData: any): Promise<OrderMutationResponse> {
    // Do not retry POST create to avoid duplicate order creation on timeouts.
    // Also deduplicate same in-flight payload submissions from UI.
    const requestKey = `create-order-in-progress:${JSON.stringify(orderData)}`;
    return deduplicatedRequest(requestKey, async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/in-progress`,
        {
          method: 'POST',
          body: JSON.stringify(orderData)
        },
        10000, // 10 second timeout
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create order: ${response.status} - ${errorText}`);
      }
      return await response.json();
    });
  },

  async deleteOrderInProgress(orderId: number): Promise<OrderMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/in-progress/${orderId}`,
        { method: 'DELETE' },
        60000, // 60 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to delete order: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getOrderDetails(orderId: number): Promise<OrderInProgressDetailResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/in-progress/${orderId}`,
        {},
        10000, // 10 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch order details: ${response.status}`);
      }
      return await response.json();
    });
  },

  async getOrderDetailForEdit(orderId: number): Promise<OrderDetailResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/order/order_detail/${orderId}`,
        {},
        10000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch order detail: ${response.status}`);
      }
      return await response.json();
    });
  },

  async updateOrderFull(orderId: number, orderData: {
    sku: string;
    product_name?: string;
    quantity: number;
    price_per_unit?: number;
    total_cost?: number;
    order_notes?: string;
    manufacturer?: string;
    allocations?: Array<{
      allocation_id: number;
      warehouse: string;
      recommended_quantity?: number;
      final_quantity: number;
      is_locked?: boolean;
    }>;
  }): Promise<OrderMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/update_orders/${orderId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(orderData)
        },
        15000,
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update order: ${response.status} - ${errorText}`);
      }
      return await response.json();
    });
  },

  async updateOrderStatus(orderId: number, status: string): Promise<OrderMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/in-progress/${orderId}/status?status=${status}`,
        { method: 'PATCH' },
        10000, // 10 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to update order status: ${response.status}`);
      }
      return await response.json();
    });
  },

  // ============ ORDER MANAGEMENT  ============

  // Get all orders from database
  async getOrders(status?: string, sku?: string): Promise<OrdersResponse> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (sku) params.append('sku', sku);

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders?${params.toString()}`,
        {},
        15000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Create a new order
  async createOrder(orderData: {
    order_number: string;
    sku: string;
    product_name?: string;
    quantity: number;
    price_per_unit?: number;
    total_cost?: number;
    order_notes?: string;
    allocations?: Array<{
      warehouse: string;
      recommended_quantity?: number;
      final_quantity: number;
      is_locked?: boolean;
    }>;
  }): Promise<OrderMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders`,
        {
          method: 'POST',
          body: JSON.stringify(orderData)
        },
        15000,
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create order: ${response.status} - ${errorText}`);
      }
      return await response.json();
    });
  },

  // Update an order (status, notes, allocations)
  async updateOrder(orderId: number, orderData: {
    status?: string;
    order_notes?: string;
    allocations?: Array<{
      warehouse: string;
      recommended_quantity?: number;
      final_quantity: number;
      is_locked?: boolean;
    }>;
  }): Promise<OrderMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/${orderId}`,
        {
          method: 'PUT',
          body: JSON.stringify(orderData)
        },
        15000,
      );
      if (!response.ok) {
        throw new Error(`Failed to update order: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Delete an order
  async deleteOrder(orderId: number): Promise<OrderMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/${orderId}`,
        { method: 'DELETE' },
        15000,
      );
      if (!response.ok) {
        throw new Error(`Failed to delete order: ${response.status}`);
      }
      return await response.json();
    });
  },

  // ============ SHIPMENT MANAGEMENT ============

  // Get all shipments from database
  async getShipments(status?: string, destination?: string, orderId?: number): Promise<ShipmentsResponse> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (destination) params.append('destination', destination);
    if (orderId) params.append('order_id', orderId.toString());

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipments?${params.toString()}`,
        {},
        30000, // 30 second timeout 
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch shipments: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Create a new shipment
  async createShipment(shipmentData: {
    order_id: number;
    order_number: string;
    sku: string;
    quantity: number;
    origin?: string;
    destination: string;
    estimated_ship_date?: string;
    estimated_arrival_date?: string;
  }): Promise<ShipmentMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipments`,
        {
          method: 'POST',
          body: JSON.stringify(shipmentData)
        },
        15000,
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create shipment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    });
  },

  // Update a shipment (status, dates, tracking, etc.)
  async updateShipment(shipmentId: number, shipmentData: {
    status?: string;
    estimated_ship_date?: string;
    estimated_arrival_date?: string;
    actual_ship_date?: string;
    actual_arrival_date?: string;
    supplier?: string;
    shipping_method?: string;
    tracking_number?: string;
    tracking_url?: string;
    tracking_entries?: Array<{
      tracking_number: string;
      tracking_url?: string | null;
      cartons_count?: number | null;
    }>;
    shipping_reference?: string;
    notes?: string;
  }): Promise<ShipmentMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipments/${shipmentId}`,
        {
          method: 'PUT',
          body: JSON.stringify(shipmentData)
        },
        15000,
      );
      if (!response.ok) {
        // Try to get error detail from response
        let errorDetail = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorDetail;
        } catch {
          // If can't parse JSON, use status text
          errorDetail = response.statusText || errorDetail;
        }
        throw new Error(errorDetail);
      }
      return await response.json();
    });
  },

  // Delete a shipment
  async deleteShipment(shipmentId: number): Promise<ShipmentMutationResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipments/${shipmentId}`,
        { method: 'DELETE' },
        15000,
      );
      if (!response.ok) {
        throw new Error(`Failed to delete shipment: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Get shipment change logs/history
  async getShipmentLogs(shipmentId: number): Promise<ShipmentLogsResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/shipments/${shipmentId}/logs`,
        { method: 'GET' },
        15000,
      );
      if (!response.ok) {
        throw new Error(`Failed to get shipment logs: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Create shipments from order allocations
  async createShipmentsFromOrder(orderId: number): Promise<CreateShipmentsFromOrderResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/orders/${orderId}/create-shipments`,
        { method: 'POST' },
        15000,
      );
      if (!response.ok) {
        throw new Error(`Failed to create shipments: ${response.status}`);
      }
      return await response.json();
    });
  },

  // Check database health
  async checkDbHealth(): Promise<DbHealthResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/db-health`,
        {},
        10000,
      );
      if (!response.ok) {
        throw new Error(`Database health check failed: ${response.status}`);
      }
      return await response.json();
    });
  },

  //PRODUCT ANALYTICS: Product mix over time
  async getProductMix(days: number = 90): Promise<ProductMixResponse> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/analytics/product-mix?days=${days}`,
        {},
        30000, // 30 second timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch product mix: ${response.status}`);
      }
      return await response.json();
    });
  },

  // PRODUCT COGS: Editable cost of goods sold per product
  async getProductCogs(): Promise<Array<{ product_name: string; display_name: string; cogs_per_order: number; status: string }>> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/product-cogs`,
        {},
        10000,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch product COGS: ${response.status}`);
      }
      return await response.json();
    });
  },

  async updateProductCog(productName: string, cogsPerOrder: number): Promise<{ product_name: string; cogs_per_order: number }> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/product-cogs/${encodeURIComponent(productName)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ cogs_per_order: cogsPerOrder }),
        },
        10000,
      );
      if (!response.ok) {
        throw new Error(`Failed to update product COGS: ${response.status}`);
      }
      return await response.json();
    });
  },

  // UNIT ECONOMICS: Global settings
  async getGlobalSettings(): Promise<{ processing_fee: number; refund_reserve: number; shipping_per_order: number; currency: string; fallback: { processing_fee: number; refund_reserve: number; shipping_per_order: number } }> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(`${API_BASE_URL}/unit-economics/global-settings`, {}, 10000);
      if (!response.ok) throw new Error(`Failed to fetch global settings: ${response.status}`);
      return await response.json();
    });
  },

  async updateGlobalSettings(payload: Partial<{ processing_fee: number; refund_reserve: number; shipping_per_order: number }>): Promise<{ processing_fee: number; refund_reserve: number; shipping_per_order: number; currency: string; fallback: { processing_fee: number; refund_reserve: number; shipping_per_order: number } }> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/unit-economics/global-settings`,
        { method: 'PATCH', body: JSON.stringify(payload) },
        10000,
      );
      if (!response.ok) throw new Error(`Failed to update global settings: ${response.status}`);
      return await response.json();
    });
  },

  // UNIT ECONOMICS: Cost profiles
  async getCostProfiles(): Promise<Array<{ id: number; name: string; shipping_per_order: number; processing_fee_override: number | null; refund_reserve_override: number | null }>> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(`${API_BASE_URL}/unit-economics/cost-profiles`, {}, 10000);
      if (!response.ok) throw new Error(`Failed to fetch cost profiles: ${response.status}`);
      return await response.json();
    });
  },

  async createCostProfile(payload: { name: string; shipping_per_order: number; processing_fee_override?: number | null; refund_reserve_override?: number | null }): Promise<{ id: number; name: string; shipping_per_order: number; processing_fee_override: number | null; refund_reserve_override: number | null }> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/unit-economics/cost-profiles`,
        { method: 'POST', body: JSON.stringify(payload) },
        10000,
      );
      if (!response.ok) throw new Error(`Failed to create cost profile: ${response.status}`);
      return await response.json();
    });
  },

  async updateCostProfile(id: number, payload: Partial<{ shipping_per_order: number; processing_fee_override: number | null; refund_reserve_override: number | null }>): Promise<{ id: number; name: string; shipping_per_order: number; processing_fee_override: number | null; refund_reserve_override: number | null }> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/unit-economics/cost-profiles/${id}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
        10000,
      );
      if (!response.ok) throw new Error(`Failed to update cost profile: ${response.status}`);
      return await response.json();
    });
  },

  async deleteCostProfile(id: number): Promise<void> {
    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/unit-economics/cost-profiles/${id}`,
        { method: 'DELETE' },
        10000,
      );
      if (!response.ok) throw new Error(`Failed to delete cost profile: ${response.status}`);
    });
  },

  // REPLENISHMENT CALENDAR: Calendar view of reorders and OOS dates
  async getReplenishmentCalendarData(
    warehouse: string = 'all',
    daysAhead: number = 90
  ): Promise<ReplenishmentCalendarResponse> {
    const params = new URLSearchParams({
      warehouse: warehouse,
      days_ahead: daysAhead.toString(),
    });

    return this.retryWithBackoff(async () => {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/replenishment/calendar?${params.toString()}`,
        {},
        60000, // 1 minute timeout
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch replenishment calendar: ${response.status}`);
      }
      return await response.json();
    });
  },
};